<script setup>
import { computed } from 'vue';
import { useTitle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';

import LegalSearchBar from '@/components/legal/LegalSearchBar.vue';
import LegalTableOfContents from '@/components/legal/LegalTableOfContents.vue';
import { useLegalSearch } from '@/composables/useLegalSearch.js';
import { useScrollSpy } from '@/composables/useScrollSpy.js';
import Icon from '@/components/Icon.vue';

import PrivacyFloatingHeader from '@/components/legal/privacy/PrivacyFloatingHeader.vue';
import PrivacyHero from '@/components/legal/privacy/PrivacyHero.vue';
import PrivacyTldrCard from '@/components/legal/privacy/PrivacyTldrCard.vue';
import PrivacySectionList from '@/components/legal/privacy/PrivacySectionList.vue';
import PrivacyUsageGrid from '@/components/legal/privacy/PrivacyUsageGrid.vue';
import PrivacyKidsCard from '@/components/legal/privacy/PrivacyKidsCard.vue';

const { t, tm } = useI18n();

useTitle(() => t('legal.privacy.metaTitle'));

const updatedAt = 'September 7, 2026';
const contactEmail = 'privacy@deenly.app';

const tocModel = computed(() => tm('legal.privacy.toc'));

const { query, filteredSections, matchCount } = useLegalSearch(
  computed(() =>
    tocModel.value.map((s) => ({
      id: s.id,
      title: s.label,
      content: [s.label, ...(s.searchText ?? [])],
    }))
  )
);

const tocItems = computed(() =>
  filteredSections.value.map((s) => ({ id: s.id, title: s.title }))
);

const spyIds = computed(() => tocItems.value.map((i) => i.id));
const { activeId } = useScrollSpy(spyIds);

const hasQuery = computed(() => query.value.trim().length > 0);

const tldrCards = computed(() => tm('legal.privacy.tldr.cards'));
const tldrIcon = computed(() => t('legal.privacy.tldr.icon'));
const collectionItems = computed(() => tm('legal.privacy.collection.items'));
const usageCards = computed(() => tm('legal.privacy.usage.cards'));
const kidsItems = computed(() => tm('legal.privacy.kids.items'));
const kidsBadge = computed(() => tm('legal.privacy.kids.badge'));

function scrollToTop() {
  window?.scrollTo?.({ top: 0, behavior: 'smooth' });
}
</script>

<template>
  <div class="antialiased relative selection:bg-yellow-main/30 selection:text-[#1c4848]">
    <div
      class="fixed inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.02] pointer-events-none z-50 mix-blend-overlay"
    />

    <PrivacyFloatingHeader
      brand="Deenly"
      :backLabel="t('legal.privacy.header.back')"
      backHref="/"
    />

    <PrivacyHero
      :badge="t('legal.privacy.header.badge')"
      :badgeIcon="t('legal.privacy.hero.badgeIcon')"
      :titlePrefix="t('legal.privacy.hero.titlePrefix')"
      :titleGradient="t('legal.privacy.hero.titleGradient')"
      :subtitle="t('legal.privacy.hero.subtitle')"
      :lastUpdatedLabel="t('legal.privacy.hero.lastUpdatedLabel')"
      :updatedAt="updatedAt"
      :calendarIcon="t('legal.privacy.hero.calendarIcon')"
    >
      <template #search>
        <div class="mt-8 max-w-xl mx-auto">
          <LegalSearchBar v-model="query" :placeholder="t('legal.privacy.search.placeholder')" />
          <div class="mt-3 text-xs text-[#1c4848]/45">
            {{ t('legal.privacy.search.found') }}:
            <span class="font-medium text-[#1c4848]">{{ matchCount }}</span>
            <button
              v-if="hasQuery"
              class="ml-3 underline decoration-[#1c4848]/20 underline-offset-2 hover:text-[#1c4848]"
              @click="query = ''"
            >
              {{ t('legal.privacy.search.clear') }}
            </button>
          </div>
        </div>
      </template>
    </PrivacyHero>

    <!-- Main content -->
    <section class="pb-32 px-6 relative">
      <div class="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 relative z-10">
        <aside class="hidden lg:block lg:col-span-3">
          <div class="sticky top-28 animate-slide-up" style="animation-delay: 0.2s;">
            <h4 class="text-xs font-medium text-[#1c4848]/40 uppercase tracking-[0.2em] mb-6 pl-4">
              {{ t('legal.privacy.search.tocTitle') }}
            </h4>
            <LegalTableOfContents :items="tocItems" :activeId="activeId" />
          </div>
        </aside>

        <div class="lg:col-span-9 max-w-2xl animate-slide-up" style="animation-delay: 0.3s;">
          <template v-if="filteredSections.length">
            <PrivacyTldrCard :title="t('legal.privacy.tldr.title')" :icon="tldrIcon" :cards="tldrCards" />

            <PrivacySectionList
              id="collection"
              :title="t('legal.privacy.collection.title')"
              :intro="t('legal.privacy.collection.intro')"
              :items="collectionItems"
            />

            <PrivacyUsageGrid
              id="usage"
              :title="t('legal.privacy.usage.title')"
              :intro="t('legal.privacy.usage.intro')"
              :cards="usageCards"
            />

            <PrivacyKidsCard
              id="kids"
              :badge="kidsBadge"
              :title="t('legal.privacy.kids.title')"
              :intro="t('legal.privacy.kids.intro')"
              :items="kidsItems"
            />

            <div id="sharing" class="mb-20 scroll-mt-32">
              <h2 class="text-3xl font-serif text-[#1c4848] font-medium tracking-tighter mb-6">
                {{ t('legal.privacy.sharing.title') }}
              </h2>
              <p class="text-base text-[#1c4848]/60 font-normal leading-relaxed p-6 rounded-2xl bg-white border border-[#1c4848]/5 shadow-sm">
                {{ t('legal.privacy.sharing.body') }}
              </p>
            </div>

            <div id="security" class="mb-20 scroll-mt-32">
              <h2 class="text-3xl font-serif text-[#1c4848] font-medium tracking-tighter mb-6">
                {{ t('legal.privacy.security.title') }}
              </h2>
              <p class="text-base text-[#1c4848]/60 font-normal leading-relaxed">
                {{ t('legal.privacy.security.body') }}
              </p>
            </div>

            <div id="contact" class="scroll-mt-32 pt-8">
              <div class="flex flex-col sm:flex-row items-center justify-between gap-8 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] bg-blend-overlay bg-[#1c4848] p-8 sm:p-10 rounded-[2rem] shadow-xl relative overflow-hidden">
                <div class="absolute inset-0 bg-gradient-to-r from-transparent to-[#ecd79f]/10 pointer-events-none"></div>

                <div class="relative z-10 text-center sm:text-left">
                  <h3 class="text-2xl font-serif text-white font-medium tracking-tighter mb-2">
                    {{ t('legal.privacy.contactCta.title') }}
                  </h3>
                  <p class="text-sm text-white/70 font-normal">
                    {{ t('legal.privacy.contactCta.subtitle') }}
                  </p>
                </div>
                <a
                  :href="`mailto:${contactEmail}`"
                  class="relative z-10 bg-white text-[#1c4848] px-6 py-3.5 rounded-xl text-sm font-medium hover:scale-105 transition-transform duration-300 flex items-center gap-2 shrink-0 shadow-lg"
                >
                  <Icon :icon="t('legal.privacy.contactCta.icon')" class="text-lg" />
                  {{ t('legal.privacy.contactCta.button') }}
                </a>
              </div>
            </div>
          </template>

          <div v-else class="p-6 rounded-2xl bg-white border border-[#1c4848]/5 shadow-sm">
            <div class="text-base font-medium text-[#1c4848]">
              {{ t('legal.privacy.search.emptyTitle') }}
            </div>
            <div class="mt-2 text-sm text-[#1c4848]/60">
              {{ t('legal.privacy.search.emptyBody') }}
            </div>
            <div class="mt-5 flex gap-3">
              <button class="px-6 py-2 rounded-xl bg-green-main text-white text-sm font-medium" @click="query = ''">
                {{ t('legal.privacy.search.clear') }}
              </button>
              <button class="px-6 py-2 rounded-xl border border-[#1c4848]/10 bg-white text-[#1c4848] text-sm font-medium" @click="scrollToTop">
                {{ t('legal.privacy.search.backToTop') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <footer class="border-t border-[#1c4848]/10 py-12 bg-white/50 backdrop-blur-lg relative z-10 mt-12">
      <div class="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
        <div class="flex items-center gap-3 opacity-50 hover:opacity-100 transition-opacity duration-300">
          <div class="w-6 h-6 rounded-md bg-[#1c4848] text-yellow-main flex items-center justify-center overflow-hidden">
            <img src="@/assets/logo.svg" alt="Deenly" class="w-4 h-4" />
          </div>
          <span class="font-serif text-[#1c4848] font-medium text-sm">Deenly</span>
        </div>

        <p class="text-xs font-normal text-[#1c4848]/40 text-center md:text-left">
          © {{ new Date().getFullYear() }} Deenly. {{ t('legal.privacy.footerNote') }}
        </p>

        <div class="flex gap-6 text-xs font-medium text-[#1c4848]/50 uppercase tracking-widest">
          <a href="#terms" class="hover:text-[#1c4848] transition-colors duration-300">
            {{ t('legal.privacy.footer.links.terms') }}
          </a>
          <a href="#collection" class="text-[#1c4848]">
            {{ t('legal.privacy.footer.links.privacy') }}
          </a>
          <a href="#sharing" class="hover:text-[#1c4848] transition-colors duration-300">
            {{ t('legal.privacy.footer.links.cookies') }}
          </a>
        </div>
      </div>
    </footer>
  </div>
</template>

